/**
 * I7-A copy (booking search, exact lookup, comment templates) in the three
 * operator languages, plus the Element Plus locale that matches them.
 *
 * The legacy catalog in `core/plugins/i18n.ts` is one mixed catalog and is NOT
 * localized as a whole; only the `i7a.*` keys below are. I7-A surfaces resolve
 * the current Vue-i18n locale to `ro | ru | en` (anything else → `ro`) and read
 * their copy in that locale, so an unrelated legacy locale never shows raw keys.
 */
import {computed, unref, type ComputedRef} from "vue";
import {createI18n} from "vue-i18n";
import elementRo from "element-plus/es/locale/lang/ro";
import elementRu from "element-plus/es/locale/lang/ru";
import elementEn from "element-plus/es/locale/lang/en";

export const I7A_LOCALES = ["ro", "ru", "en"] as const;
export type I7aLocale = typeof I7A_LOCALES[number];
export const I7A_DEFAULT_LOCALE: I7aLocale = "ro";

type Catalog = {[key: string]: string | Catalog};

const ro: Catalog = {
  element: {
    dialogClose: "Închide fereastra",
  },
  search: {
    label: "Caută rezervări",
    placeholder: "ID, nume, pasager sau email",
    hintMin: "Introduceți cel puțin 2 caractere.",
    hintMax: "Maximum 100 de caractere.",
    clear: "Golește căutarea",
    lookup: "Telefon sau ID exact",
    lookupHint: "caută în afara filtrelor listei",
    lookupAria: "Căutare exactă după telefon sau ID, în afara filtrelor curente ale listei",
  },
  list: {
    loading: "Se încarcă rezervările…",
    empty: "Nu există rezervări pentru filtrele selectate.",
    zeroTitle: "Niciun rezultat pentru „{q}”",
    zeroHint: "Verificați termenul sau goliți căutarea. Filtrele de dată și statut rămân aplicate.",
    clearSearch: "Golește căutarea",
    errorTitle: "Rezervările nu au putut fi încărcate.",
    errorStale: "Lista de mai jos poate fi neactualizată.",
    retry: "Reîncearcă",
    announceCount: "Rezervări găsite: {count}.",
    announceZero: "Nicio rezervare găsită.",
    announceError: "Eroare la încărcarea rezervărilor.",
    exportFailed: "Exportul nu a reușit. Încercați din nou.",
    exportBlocked: "Exportul în Excel este disponibil după ce lista se încarcă din nou cu succes.",
  },
  lookup: {
    title: "Căutare exactă",
    intro: "Caută în toate rezervările la care aveți acces, indiferent de perioada și filtrele listei.",
    modeLegend: "Caută după",
    modeId: "ID rezervare",
    modePhone: "Telefon",
    idLabel: "ID rezervare",
    idPlaceholder: "ex. 123456",
    phoneLabel: "Număr de telefon",
    phonePlaceholder: "ex. +373 69 123 456",
    submit: "Caută",
    searching: "Se caută…",
    loadMore: "Încarcă mai multe",
    noResults: "Nicio rezervare găsită.",
    resultsCount: "Rezultate afișate: {count}",
    openBooking: "Deschide rezervarea #{id}",
    close: "Închide",
    fieldDate: "Plecare",
    fieldRoute: "Rută",
    fieldPassenger: "Pasager",
    fieldStatus: "Statut",
    invalidId: "Introduceți un ID numeric pozitiv.",
    invalidPhone: "Telefonul trebuie să aibă între 3 și 64 de caractere.",
  },
  templates: {
    insert: "Inserează șablon",
    insertInto: {
      public: "în comentariul public",
      private: "în comentariul privat",
    },
    panelLabel: "Șabloane de comentarii",
    loading: "Se încarcă șabloanele…",
    empty: "Nu există șabloane active.",
    retry: "Reîncearcă",
    badgeCompany: "Companie",
    badgeGlobal: "Global",
    readOnly: "doar citire",
    manage: "Gestionează șabloane",
    close: "Închide",
  },
  manager: {
    title: "Șabloane de comentarii",
    scope: "Șabloanele companiei curente",
    loading: "Se încarcă șabloanele…",
    empty: "Compania nu are încă șabloane.",
    newLabel: "Șablon nou",
    newPlaceholder: "Textul care va fi inserat în comentariu",
    add: "Adaugă șablon",
    editLabel: "Editează șablonul",
    edit: "Editează",
    save: "Salvează",
    cancel: "Renunță",
    delete: "Șterge",
    activate: "Activează",
    deactivate: "Dezactivează",
    active: "Activ",
    inactive: "Inactiv",
    moveUp: "Mută mai sus: {excerpt}",
    moveDown: "Mută mai jos: {excerpt}",
    saveOrder: "Salvează ordinea",
    resetOrder: "Anulează ordinea",
    orderUnsaved: "Ordinea nouă nu este salvată.",
    orderLocksEditing: "Salvați sau anulați ordinea înainte de alte modificări.",
    counter: "{count} / 2000 caractere",
    tooShort: "Textul nu poate fi gol.",
    tooLong: "Textul depășește 2000 de caractere.",
    deleteTitle: "Ștergeți șablonul?",
    deleteConfirm: "Șablonul „{excerpt}” va fi șters definitiv.",
    deleteOk: "Șterge",
    deleteCancel: "Renunță",
    created: "Șablonul a fost adăugat.",
    saved: "Șablonul a fost salvat.",
    deleted: "Șablonul a fost șters.",
    reordered: "Ordinea a fost salvată.",
    activated: "Șablonul a fost activat.",
    deactivated: "Șablonul a fost dezactivat.",
    busy: "Se salvează…",
    retry: "Reîncearcă",
    close: "Închide",
    staleAfterWrite: "Modificarea a fost salvată, dar lista actualizată nu a putut fi încărcată. Reîncărcați lista înainte de alte modificări.",
    staleList: "Lista actualizată nu a putut fi încărcată. Reîncărcați lista înainte de alte modificări.",
    reloadList: "Reîncarcă lista",
    listReloaded: "Lista a fost reîncărcată.",
  },
  errors: {
    generic: "A apărut o eroare. Încercați din nou.",
    network: "Serverul nu a putut fi contactat. Verificați conexiunea.",
    forbidden: "Nu aveți permisiunea pentru această acțiune.",
    throttled: "Prea multe căutări într-un timp scurt. Așteptați un minut.",
    search_too_short: "Căutarea trebuie să aibă cel puțin 2 caractere.",
    search_too_long: "Căutarea poate avea cel mult 100 de caractere.",
    search_invalid: "Termenul de căutare nu este valid.",
    invalid_date_range: "Perioada poate avea cel mult 366 de zile, iar data de început nu poate fi după cea de sfârșit.",
    invalid_date: "Data selectată nu este validă.",
    invalid_cursor: "Paginarea a expirat. Porniți din nou căutarea.",
    invalid_booking_id: "Introduceți un ID numeric pozitiv.",
    invalid_phone: "Telefonul trebuie să aibă între 3 și 64 de caractere.",
    invalid_limit: "Numărul de rezultate cerut nu este valid.",
    lookup_exactly_one_required: "Alegeți fie ID-ul, fie telefonul.",
    template_text_required: "Textul nu poate fi gol.",
    template_text_too_long: "Textul depășește 2000 de caractere.",
    template_not_found: "Șablonul nu mai există. Lista a fost reîncărcată.",
    template_reorder_set_mismatch: "Lista de șabloane s-a schimbat între timp. Ordinea nu a fost salvată; am reîncărcat ordinea serverului.",
    template_scope_forbidden: "Nu puteți gestiona șabloanele acestei companii.",
    invalid_company: "Compania curentă nu este validă.",
    invalid_template_id: "Șablonul selectat nu este valid.",
    invalid_is_active: "Starea șablonului nu este validă.",
    scope_missing: "Compania curentă nu poate fi determinată. Reautentificați-vă.",
    session_changed: "Sesiunea s-a schimbat. Deschideți din nou lista de șabloane.",
  },
};

const ru: Catalog = {
  element: {
    dialogClose: "Закрыть окно",
  },
  search: {
    label: "Поиск бронирований",
    placeholder: "ID, имя, пассажир или email",
    hintMin: "Введите не менее 2 символов.",
    hintMax: "Не более 100 символов.",
    clear: "Очистить поиск",
    lookup: "Телефон или точный ID",
    lookupHint: "поиск вне фильтров списка",
    lookupAria: "Точный поиск по телефону или ID вне текущих фильтров списка",
  },
  list: {
    loading: "Загрузка бронирований…",
    empty: "Нет бронирований по выбранным фильтрам.",
    zeroTitle: "Ничего не найдено по запросу «{q}»",
    zeroHint: "Проверьте запрос или очистите поиск. Фильтры по дате и статусу остаются в силе.",
    clearSearch: "Очистить поиск",
    errorTitle: "Не удалось загрузить бронирования.",
    errorStale: "Список ниже может быть неактуальным.",
    retry: "Повторить",
    announceCount: "Найдено бронирований: {count}.",
    announceZero: "Бронирования не найдены.",
    announceError: "Ошибка загрузки бронирований.",
    exportFailed: "Не удалось выполнить экспорт. Попробуйте ещё раз.",
    exportBlocked: "Экспорт в Excel станет доступен после успешной загрузки списка.",
  },
  lookup: {
    title: "Точный поиск",
    intro: "Поиск по всем доступным вам бронированиям, независимо от периода и фильтров списка.",
    modeLegend: "Искать по",
    modeId: "ID бронирования",
    modePhone: "Телефон",
    idLabel: "ID бронирования",
    idPlaceholder: "напр. 123456",
    phoneLabel: "Номер телефона",
    phonePlaceholder: "напр. +373 69 123 456",
    submit: "Найти",
    searching: "Идёт поиск…",
    loadMore: "Показать ещё",
    noResults: "Бронирования не найдены.",
    resultsCount: "Показано результатов: {count}",
    openBooking: "Открыть бронирование #{id}",
    close: "Закрыть",
    fieldDate: "Отправление",
    fieldRoute: "Маршрут",
    fieldPassenger: "Пассажир",
    fieldStatus: "Статус",
    invalidId: "Введите положительный числовой ID.",
    invalidPhone: "Телефон должен содержать от 3 до 64 символов.",
  },
  templates: {
    insert: "Вставить шаблон",
    insertInto: {
      public: "в публичный комментарий",
      private: "в приватный комментарий",
    },
    panelLabel: "Шаблоны комментариев",
    loading: "Загрузка шаблонов…",
    empty: "Нет активных шаблонов.",
    retry: "Повторить",
    badgeCompany: "Компания",
    badgeGlobal: "Общий",
    readOnly: "только чтение",
    manage: "Управлять шаблонами",
    close: "Закрыть",
  },
  manager: {
    title: "Шаблоны комментариев",
    scope: "Шаблоны текущей компании",
    loading: "Загрузка шаблонов…",
    empty: "У компании пока нет шаблонов.",
    newLabel: "Новый шаблон",
    newPlaceholder: "Текст, который будет вставлен в комментарий",
    add: "Добавить шаблон",
    editLabel: "Редактирование шаблона",
    edit: "Изменить",
    save: "Сохранить",
    cancel: "Отмена",
    delete: "Удалить",
    activate: "Включить",
    deactivate: "Отключить",
    active: "Активен",
    inactive: "Отключён",
    moveUp: "Переместить выше: {excerpt}",
    moveDown: "Переместить ниже: {excerpt}",
    saveOrder: "Сохранить порядок",
    resetOrder: "Отменить порядок",
    orderUnsaved: "Новый порядок не сохранён.",
    orderLocksEditing: "Сохраните или отмените порядок перед другими изменениями.",
    counter: "{count} / 2000 символов",
    tooShort: "Текст не может быть пустым.",
    tooLong: "Текст длиннее 2000 символов.",
    deleteTitle: "Удалить шаблон?",
    deleteConfirm: "Шаблон «{excerpt}» будет удалён без возможности восстановления.",
    deleteOk: "Удалить",
    deleteCancel: "Отмена",
    created: "Шаблон добавлен.",
    saved: "Шаблон сохранён.",
    deleted: "Шаблон удалён.",
    reordered: "Порядок сохранён.",
    activated: "Шаблон включён.",
    deactivated: "Шаблон отключён.",
    busy: "Сохранение…",
    retry: "Повторить",
    close: "Закрыть",
    staleAfterWrite: "Изменение сохранено, но обновлённый список не удалось загрузить. Перезагрузите список перед другими изменениями.",
    staleList: "Обновлённый список не удалось загрузить. Перезагрузите список перед другими изменениями.",
    reloadList: "Перезагрузить список",
    listReloaded: "Список перезагружен.",
  },
  errors: {
    generic: "Произошла ошибка. Попробуйте ещё раз.",
    network: "Не удалось связаться с сервером. Проверьте подключение.",
    forbidden: "У вас нет прав на это действие.",
    throttled: "Слишком много запросов за короткое время. Подождите минуту.",
    search_too_short: "Поисковый запрос должен содержать не менее 2 символов.",
    search_too_long: "Поисковый запрос может содержать не более 100 символов.",
    search_invalid: "Недопустимый поисковый запрос.",
    invalid_date_range: "Период может включать не более 366 дней, а начальная дата не может быть позже конечной.",
    invalid_date: "Выбранная дата недействительна.",
    invalid_cursor: "Постраничная выдача устарела. Запустите поиск заново.",
    invalid_booking_id: "Введите положительный числовой ID.",
    invalid_phone: "Телефон должен содержать от 3 до 64 символов.",
    invalid_limit: "Недопустимое количество результатов.",
    lookup_exactly_one_required: "Выберите либо ID, либо телефон.",
    template_text_required: "Текст не может быть пустым.",
    template_text_too_long: "Текст длиннее 2000 символов.",
    template_not_found: "Шаблон больше не существует. Список обновлён.",
    template_reorder_set_mismatch: "Список шаблонов изменился. Порядок не сохранён; загружен порядок с сервера.",
    template_scope_forbidden: "Вы не можете управлять шаблонами этой компании.",
    invalid_company: "Текущая компания недействительна.",
    invalid_template_id: "Выбранный шаблон недействителен.",
    invalid_is_active: "Недопустимый статус шаблона.",
    scope_missing: "Не удалось определить текущую компанию. Войдите заново.",
    session_changed: "Сессия изменилась. Откройте список шаблонов снова.",
  },
};

const en: Catalog = {
  element: {
    dialogClose: "Close this dialog",
  },
  search: {
    label: "Search bookings",
    placeholder: "ID, name, passenger or email",
    hintMin: "Enter at least 2 characters.",
    hintMax: "Maximum 100 characters.",
    clear: "Clear search",
    lookup: "Exact phone or ID",
    lookupHint: "searches outside the list filters",
    lookupAria: "Exact lookup by phone or ID, outside the current list filters",
  },
  list: {
    loading: "Loading bookings…",
    empty: "No bookings match the selected filters.",
    zeroTitle: "No results for “{q}”",
    zeroHint: "Check the term or clear the search. Date and status filters still apply.",
    clearSearch: "Clear search",
    errorTitle: "Bookings could not be loaded.",
    errorStale: "The list below may be out of date.",
    retry: "Retry",
    announceCount: "Bookings found: {count}.",
    announceZero: "No bookings found.",
    announceError: "Error loading bookings.",
    exportFailed: "The export failed. Please try again.",
    exportBlocked: "Excel export is available once the list loads successfully again.",
  },
  lookup: {
    title: "Exact lookup",
    intro: "Searches every booking you can access, regardless of the list period and filters.",
    modeLegend: "Search by",
    modeId: "Booking ID",
    modePhone: "Phone",
    idLabel: "Booking ID",
    idPlaceholder: "e.g. 123456",
    phoneLabel: "Phone number",
    phonePlaceholder: "e.g. +373 69 123 456",
    submit: "Search",
    searching: "Searching…",
    loadMore: "Load more",
    noResults: "No bookings found.",
    resultsCount: "Results shown: {count}",
    openBooking: "Open booking #{id}",
    close: "Close",
    fieldDate: "Departure",
    fieldRoute: "Route",
    fieldPassenger: "Passenger",
    fieldStatus: "Status",
    invalidId: "Enter a positive numeric ID.",
    invalidPhone: "The phone must have between 3 and 64 characters.",
  },
  templates: {
    insert: "Insert template",
    insertInto: {
      public: "into the public comment",
      private: "into the private comment",
    },
    panelLabel: "Comment templates",
    loading: "Loading templates…",
    empty: "No active templates.",
    retry: "Retry",
    badgeCompany: "Company",
    badgeGlobal: "Global",
    readOnly: "read-only",
    manage: "Manage templates",
    close: "Close",
  },
  manager: {
    title: "Comment templates",
    scope: "Current company templates",
    loading: "Loading templates…",
    empty: "The company has no templates yet.",
    newLabel: "New template",
    newPlaceholder: "Text that will be inserted into the comment",
    add: "Add template",
    editLabel: "Edit template",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    activate: "Activate",
    deactivate: "Deactivate",
    active: "Active",
    inactive: "Inactive",
    moveUp: "Move up: {excerpt}",
    moveDown: "Move down: {excerpt}",
    saveOrder: "Save order",
    resetOrder: "Discard order",
    orderUnsaved: "The new order is not saved.",
    orderLocksEditing: "Save or discard the order before other changes.",
    counter: "{count} / 2000 characters",
    tooShort: "The text cannot be empty.",
    tooLong: "The text exceeds 2000 characters.",
    deleteTitle: "Delete template?",
    deleteConfirm: "The template “{excerpt}” will be permanently deleted.",
    deleteOk: "Delete",
    deleteCancel: "Cancel",
    created: "Template added.",
    saved: "Template saved.",
    deleted: "Template deleted.",
    reordered: "Order saved.",
    activated: "Template activated.",
    deactivated: "Template deactivated.",
    busy: "Saving…",
    retry: "Retry",
    close: "Close",
    staleAfterWrite: "The change was saved, but the updated list could not be loaded. Reload the list before making other changes.",
    staleList: "The updated list could not be loaded. Reload the list before making other changes.",
    reloadList: "Reload list",
    listReloaded: "The list was reloaded.",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "The server could not be reached. Check your connection.",
    forbidden: "You do not have permission for this action.",
    throttled: "Too many searches in a short time. Please wait a minute.",
    search_too_short: "The search must have at least 2 characters.",
    search_too_long: "The search can have at most 100 characters.",
    search_invalid: "The search term is not valid.",
    invalid_date_range: "The period can cover at most 366 days, and the start date cannot be after the end date.",
    invalid_date: "The selected date is not valid.",
    invalid_cursor: "The result pages expired. Start the search again.",
    invalid_booking_id: "Enter a positive numeric ID.",
    invalid_phone: "The phone must have between 3 and 64 characters.",
    invalid_limit: "The requested number of results is not valid.",
    lookup_exactly_one_required: "Choose either the ID or the phone.",
    template_text_required: "The text cannot be empty.",
    template_text_too_long: "The text exceeds 2000 characters.",
    template_not_found: "The template no longer exists. The list was reloaded.",
    template_reorder_set_mismatch: "The template list changed meanwhile. The order was not saved; the server order was reloaded.",
    template_scope_forbidden: "You cannot manage this company's templates.",
    invalid_company: "The current company is not valid.",
    invalid_template_id: "The selected template is not valid.",
    invalid_is_active: "The template state is not valid.",
    scope_missing: "The current company cannot be determined. Please sign in again.",
    session_changed: "The session changed. Open the template list again.",
  },
};

export const I7A_MESSAGES: Readonly<Record<I7aLocale, Catalog>> = {ro, ru, en};

const VENDOR_ELEMENT_LOCALES = {ro: elementRo, ru: elementRu, en: elementEn} as const;

/**
 * The installed Element Plus RO/RU packs lack `el.dialog.close` (the dialog
 * close button's accessible name), so it would read literally
 * "el.dialog.close". I7-A surfaces get a copy of each pack with only that key
 * supplied from the I7-A catalog; the shared vendor objects are never mutated
 * and every other field is kept as imported.
 */
function withDialogClose(base: typeof elementRo, locale: I7aLocale): typeof elementRo {
  const el = base.el as Record<string, unknown>;
  const dialog = (el.dialog || {}) as Record<string, unknown>;
  const close = (I7A_MESSAGES[locale].element as Catalog).dialogClose as string;
  return Object.freeze({
    ...base,
    el: {...el, dialog: {...dialog, close}},
  }) as unknown as typeof elementRo;
}

const ELEMENT_LOCALES: Readonly<Record<I7aLocale, typeof elementRo>> = {
  ro: withDialogClose(VENDOR_ELEMENT_LOCALES.ro, "ro"),
  ru: withDialogClose(VENDOR_ELEMENT_LOCALES.ru, "ru"),
  en: withDialogClose(VENDOR_ELEMENT_LOCALES.en, "en"),
};

export function resolveI7aLocale(value: unknown): I7aLocale {
  return (I7A_LOCALES as readonly unknown[]).includes(value) ? (value as I7aLocale) : I7A_DEFAULT_LOCALE;
}

export function elementLocaleFor(value: unknown): typeof elementRo {
  return ELEMENT_LOCALES[resolveI7aLocale(value)];
}

/**
 * Adds the `i7a` namespace to the application catalog without touching any
 * legacy key: `en` gains it next to its legacy keys, `ro`/`ru` hold only it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withI7aMessages<T extends Record<string, any>>(messages: T): T & Record<I7aLocale, Catalog> {
  const merged: Record<string, Catalog> = {...messages};
  for (const locale of I7A_LOCALES) {
    merged[locale] = {...(merged[locale] || {}), i7a: I7A_MESSAGES[locale]};
  }
  return merged as T & Record<I7aLocale, Catalog>;
}

// The application instance registers itself from `core/plugins/i18n.ts` (no
// import cycle). Legacy component tests that mount I7-A hosts without that
// plugin get a private instance limited to the I7-A catalog.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlobalComposer = any;
let registered: {global: GlobalComposer} | null = null;
let fallback: {global: GlobalComposer} | null = null;

export function registerI7aI18n(instance: {global: GlobalComposer}): void {
  registered = instance;
}

function composer(): GlobalComposer {
  if (registered) return registered.global;
  if (!fallback) {
    fallback = createI18n({legacy: false, locale: I7A_DEFAULT_LOCALE, messages: withI7aMessages({})});
  }
  return fallback.global;
}

export interface I7aI18n {
  t: (key: string, named?: Record<string, unknown>) => string;
  tError: (code: string | null | undefined) => string;
  locale: ComputedRef<I7aLocale>;
  elementLocale: ComputedRef<typeof elementRo>;
}

export function useI7a(): I7aI18n {
  const global = composer();
  const locale = computed(() => resolveI7aLocale(unref(global.locale)));
  const elementLocale = computed(() => elementLocaleFor(locale.value));
  const t = (key: string, named: Record<string, unknown> = {}): string =>
    global.t(`i7a.${key}`, named, {locale: locale.value});
  // Stable backend codes map to the bounded catalog; anything unknown falls
  // back to generic copy, so server prose is never rendered.
  const tError = (code: string | null | undefined): string => {
    const key = `i7a.errors.${code || "generic"}`;
    return global.te(key, locale.value) ? global.t(key, {}, {locale: locale.value}) : t("errors.generic");
  };
  return {t, tError, locale, elementLocale};
}
